import React, { memo, useCallback, useEffect, useState } from 'react';
import Button from 'app/components/common/Button';
import InputCheckBox from 'app/components/common/InputCheckBox';
import classnames from 'classnames/bind';
import mobileAppBridge from 'app/libs/MobileAppBridge';
import IconArrow from 'app/components/common/IconArrow';
import useSsrUserAgent from 'app/hook/common/useSsrUserAgent';
import styles from './TermsComm.module.scss';

const cx = classnames.bind(styles);

const TermsComm = ({
	customArea = null,
	allCheckLabel = '',
	useAllCheck = false,
	list = [],
	hideOkButton = false,
	onOk = undefined,
	onChange = undefined,
	validationText = '',
	extraAllCheckLabelId = undefined,
	okButtonText = '확인',
	dtEventnamegroup = '약관동의',
	disabled = false,
	beforeGoLink = undefined,
}) => {
	const ua = useSsrUserAgent();
	const [terms, setTerms] = useState(new Map());
	const [allTerms, setAllTerms] = useState(false);
	const [validation, setValidation] = useState(false);

	const checkValidation = useCallback(() => {
		const requiredTerms = list.filter(({ isRequired }) => isRequired).map(({ name }) => name);

		return requiredTerms.every((name) => terms.get(name));
	}, [list, terms]);

	useEffect(() => {
		if (terms.size) {
			return;
		}

		if (list.filter((item) => item.isRequired).every((item) => item.defaultValue)) {
			setAllTerms(true);
		}

		list.forEach(({ name, defaultValue }) => {
			terms.set(name, defaultValue || false);
		});

		setTerms(new Map(terms));
	}, [list, terms]);

	const handleOk = () => {
		if (!checkValidation()) {
			setValidation(true);

			return;
		}

		setValidation(false);

		if (onOk) {
			onOk(
				Array.from(terms.entries()).reduce(
					(prev, [key, value]) => ({
						...prev,
						[key]: value,
					}),
					{},
				),
			);
		}
	};
	const handleChangeAllTerms = (e) => {
		const { checked } = e.target;

		list.forEach(({ name, children }) => {
			if (onChange) {
				onChange({ name, checked });
			}
			terms.set(name, checked);
			if (children) {
				children.forEach((child) => terms.set(child, checked));
			}
		});

		setTerms(new Map(terms));
		setAllTerms(checked);
	};

	const handleChangeTerm = (e) => {
		const { name, checked } = e.currentTarget;
		if (onChange) {
			onChange({ name, checked });
		}

		setTerms((originTerms) => {
			originTerms.set(name, checked);

			const targetItem = list.find((v) => v.name === name);
			const { children } = targetItem || { children: undefined };

			const isParent = Array.isArray(children);
			if (isParent) {
				// parent의 children들을 다 체크상태로
				children.forEach((child) => {
					originTerms.set(child, checked);
				});
			}

			const isChildren = !!targetItem.parentId;
			if (isChildren) {
				// children 체크시,부모 체크 여부 결정
				const isAllChildSiblingChecked = targetItem.sibling.every((v) => originTerms.get(v));
				originTerms.set(targetItem.parentId, checked && isAllChildSiblingChecked);
			}

			if (Array.from(originTerms.values()).every(Boolean)) {
				setAllTerms(true);
			} else {
				setAllTerms(false);
			}

			return new Map(originTerms);
		});
	};

	const handleClickLink = (link) => (e) => {
		const { isApp } = ua();
		const isTargetBlank = e.target.target === '_blank';

		if (beforeGoLink) {
			beforeGoLink();
		}

		if (isApp && isTargetBlank) {
			e.preventDefault();
			const fullUrlLink = link.includes('http') ? link : `${window.location.origin}${link}`;
			mobileAppBridge().goNativeWindow(fullUrlLink, 'get', '');
		}
	};

	return (
		<>
			{customArea?.top}
			{useAllCheck && (
				<InputCheckBox
					id={extraAllCheckLabelId || 'allTerms'}
					name="allTerms"
					value={allTerms}
					label={allCheckLabel || '약관 전체동의'}
					labelSize="16"
					onChange={handleChangeAllTerms}
					data-enlog-dt-eventnamegroup={dtEventnamegroup}
					disabled={disabled}
				/>
			)}
			<ul className={cx('list', { line: useAllCheck })}>
				{list.map(({ name, label, link, target, isChildren, children }) => (
					<li
						key={name}
						className={cx({
							none_link: !link,
							children_term: link && isChildren,
						})}
					>
						<InputCheckBox
							id={name}
							name={name}
							value={terms.get(name) || false}
							label={label}
							onChange={handleChangeTerm}
							data-enlog-dt-eventnamegroup={dtEventnamegroup}
							disabled={disabled}
						/>
						{!children && link && (
							<a
								href={link}
								className={cx('link')}
								target={target}
								onClick={handleClickLink(link)}
							>
								약관보기
								<IconArrow
									style={{ marginLeft: 5 }}
									size="s"
									direction="right"
								/>
							</a>
						)}
					</li>
				))}
			</ul>
			{customArea?.bottom}
			{!checkValidation() && validation && <p className={cx('error_text')}>{validationText || '약관에 동의해주세요.'}</p>}
			{!hideOkButton && (
				<Button
					style={{ marginTop: 20 }}
					title={okButtonText}
					domType="button"
					color={checkValidation() ? 'red' : 'silver'}
					onClick={handleOk}
					data-enlog-dt-eventnamegroup={dtEventnamegroup}
				/>
			)}
		</>
	);
};

export default memo(TermsComm);
